import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import axiosInstance from '../../services/axiosInstance'
import { Plus, Users, Search, Folder, Send } from 'lucide-react'
import PageHeader from '../../components/common/PageHeader'
import CreateStudentModal from '../../components/modals/CreateStudentModal'
import './styles.scss'

export default function AgentDashboard() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeApplications: 0,
    accepted: 0
  })
  const [recentApplications, setRecentApplications] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const [statsRes, appsRes] = await Promise.all([
          axiosInstance.get('/api/dashboard/stats'),
          axiosInstance.get('/api/applications/admin/all')
        ])
        
        if (statsRes.data?.data?.statCards) {
          const fetchedStats = statsRes.data.data.statCards
          setStats({
            totalStudents: fetchedStats.totalApplications,
            activeApplications: fetchedStats.underReview,
            accepted: fetchedStats.admitted
          })
        }
        
        if (appsRes.data?.success) {
          setRecentApplications(appsRes.data.data)
        }
      } catch (err) {
        console.error("Failed to fetch agent dashboard data:", err)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const handleCreateApplication = async (data) => {
    try {
      await axiosInstance.post('/api/applications/agent/create', data)
      setSuccessMsg('Application successfully created! Login credentials have been emailed to the student.')
      
      // Refresh the applications and stats
      const [statsRes, appsRes] = await Promise.all([
        axiosInstance.get('/api/dashboard/stats'),
        axiosInstance.get('/api/applications/admin/all')
      ])
      if (statsRes.data?.data?.statCards) {
        const fetchedStats = statsRes.data.data.statCards
        setStats({
          totalStudents: fetchedStats.totalApplications,
          activeApplications: fetchedStats.underReview,
          accepted: fetchedStats.admitted
        })
      }
      if (appsRes.data?.success) {
        setRecentApplications(appsRes.data.data)
      }
      
      setTimeout(() => setSuccessMsg(''), 5000)
    } catch (err) {
      setErrorMsg(err.response?.data?.message || 'Failed to create application.')
      setTimeout(() => setErrorMsg(''), 5000)
    }
  }

  return (
    <div className="agent-dashboard">
      <PageHeader 
        title={`Welcome back, ${user?.fullName || 'Agent'}!`} 
        subtitle="Manage your students and their university applications." 
      />

      {successMsg && (
        <div className="alert alert--success">
          <span className="alert-prefix">Success:</span> {successMsg}
        </div>
      )}

      {errorMsg && (
        <div className="alert alert--error">
          <span className="alert-prefix">Error:</span> {errorMsg}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Students</div>
          <div className="stat-value">{stats.totalStudents}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Active Applications</div>
          <div className="stat-value primary">{stats.activeApplications}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Accepted</div>
          <div className="stat-value success">{stats.accepted}</div>
        </div>
      </div>

      {(!user?.organizationIds || user.organizationIds.length === 0) ? (
        <div className="action-banner" style={{ background: '#f8fafc', border: '1px dashed #cbd5e1', alignItems: 'center', textAlign: 'center' }}>
          <div className="icon-wrapper" style={{ background: '#e2e8f0', color: '#64748b' }}>
            <Folder size={32} />
          </div>
          <h3 style={{ color: '#475569' }}>Awaiting University Assignment</h3>
          <p style={{ color: '#64748b' }}>
            You have not been assigned to a university team yet. Once an administrator assigns you to a university, you will be able to start submitting applications for your students.
          </p>
        </div>
      ) : (
        stats.totalStudents === 0 && !loading && (
          <div className="action-banner">
            <div className="icon-wrapper">
              <Folder size={32} />
            </div>
            <h3>Start a New Application</h3>
            <p>
              Ready to submit an application for one of your students? Click below to begin the process. We will automatically email the student their login credentials.
            </p>
            <button className="btn btn-primary create-btn" onClick={() => setIsCreateModalOpen(true)}>
              <Plus size={20} className="btn-icon" />
              Create Application
            </button>
          </div>
        )
      )}

      {/* Recent Applications Section */}
      <div className="dashboard-section" style={{ marginTop: '2rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ color: '#1e293b', fontSize: '1.25rem', margin: 0 }}>Recent Applications</h3>
          {stats.totalStudents > 0 && user?.organizationIds && user.organizationIds.length > 0 && (
            <button className="btn btn-primary" onClick={() => setIsCreateModalOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '6px', fontSize: '0.9rem', cursor: 'pointer', border: 'none' }}>
              <Plus size={16} />
              Start a New Application
            </button>
          )}
        </div>
        <div className="table-container" style={{ background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading...</div>
          ) : recentApplications.length > 0 ? (
            <table className="data-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', textAlign: 'left' }}>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>App Number</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>Student Name</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>Program</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontSize: '0.85rem', fontWeight: 600 }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentApplications.map((app, idx) => (
                  <tr 
                    key={idx} 
                    onClick={() => navigate(`/applications/${app.application?.id}`)}
                    style={{ borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background-color 0.2s' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '12px 16px', color: '#0f172a', fontWeight: 500 }}>
                      {app.application?.applicationNumber || 'N/A'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {app.studentName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px 16px', color: '#475569' }}>
                      {app.programName || 'N/A'}
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span className="badge badge--neutral" style={{ fontSize: '0.75rem', padding: '4px 8px', borderRadius: '4px', background: '#f1f5f9', color: '#475569' }}>
                        {(app.application?.status || 'STARTED').replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
              <Folder size={48} style={{ opacity: 0.2, margin: '0 auto 1rem' }} />
              <p>You haven't submitted any applications yet.</p>
            </div>
          )}
        </div>
      </div>

      <CreateStudentModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleCreateApplication}
      />
    </div>
  )
}
